(function () {
  const ROOT_SELECTOR = '[data-yokaa-style-root]';
  const MAX_ITEMS = 3;

  function money(cents, format) {
    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(cents, format || window.theme?.moneyFormat);
    }
    return (cents / 100).toFixed(2);
  }

  function optionName(option) {
    return typeof option === 'string' ? option : option?.name;
  }

  function sizeOptionIndex(product) {
    if (!product.options?.length) return -1;
    return product.options.findIndex((o) =>
      /^(size|مقاس|المقاس)$/i.test(String(optionName(o) || '').trim())
    );
  }

  function getSizeValues(product, sizeIdx) {
    const opt = product.options[sizeIdx];
    if (opt && typeof opt === 'object' && opt.values?.length) {
      return opt.values;
    }
    const key = `option${sizeIdx + 1}`;
    return [...new Set((product.variants || []).map((v) => v[key]).filter(Boolean))];
  }

  function variantsForProduct(product) {
    return (product.variants || []).filter((v) => v.available !== false);
  }

  function pickVariant(product, sizeValue) {
    const variants = variantsForProduct(product);
    if (!variants.length) return null;

    const sizeIdx = sizeOptionIndex(product);
    if (sizeIdx === -1 || !sizeValue) return variants[0];

    const key = `option${sizeIdx + 1}`;
    return variants.find((v) => v[key] === sizeValue) || variants[0];
  }

  function uniqueProducts(list, excludeId) {
    const seen = new Set();
    return list.filter((p) => {
      if (!p?.id || p.id === excludeId || seen.has(p.id)) return false;
      seen.add(p.id);
      return true;
    });
  }

  async function fetchIntent(productId, intent, limit) {
    const url = `/recommendations/products.json?product_id=${productId}&limit=${limit}&intent=${intent}`;
    const res = await fetch(url);
    if (!res.ok) return [];
    const data = await res.json();
    return data.products || [];
  }

  async function loadSmartProducts(productId, fallback) {
    let items = [];

    for (const intent of ['complementary', 'related']) {
      const batch = await fetchIntent(productId, intent, MAX_ITEMS + 2);
      items = uniqueProducts(items.concat(batch), Number(productId));
      if (items.length >= MAX_ITEMS) break;
    }

    if (items.length < MAX_ITEMS && Array.isArray(fallback)) {
      items = uniqueProducts(items.concat(fallback), Number(productId));
    }

    return items.slice(0, MAX_ITEMS);
  }

  function buildSizeSelect(product, index) {
    const sizeIdx = sizeOptionIndex(product);
    if (sizeIdx === -1) return '';

    const values = getSizeValues(product, sizeIdx);
    if (values.length <= 1) return '';

    const optionsHtml = values.map((val) => `<option value="${val}">${val}</option>`).join('');
    return `<select class="yokaa-style__size" data-yokaa-size="${index}" aria-label="Size">${optionsHtml}</select>`;
  }

  function renderProductRow(product, index, moneyFormat) {
    const variant = pickVariant(product);
    const img =
      product.featured_image ||
      product.images?.[0] ||
      'https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-image_large.png';
    const price = variant?.price ?? product.price;
    const sizeSelect = buildSizeSelect(product, index);
    const title = product.title || '';

    return `
      <div class="yokaa-style__item" data-yokaa-item="${index}" data-product-id="${product.id}">
        <input type="checkbox" class="yokaa-style__check" data-yokaa-check="${index}" checked aria-label="Include ${title}">
        <a class="yokaa-style__img-wrap" href="${product.url}">
          <img class="yokaa-style__img" src="${img}" alt="${title}" loading="lazy" width="72" height="72">
        </a>
        <div class="yokaa-style__info">
          <p class="yokaa-style__name"><a href="${product.url}">${title}</a></p>
          <div class="yokaa-style__meta">
            ${sizeSelect}
            <span class="yokaa-style__price" data-yokaa-price="${index}">${money(price, moneyFormat)}</span>
          </div>
        </div>
      </div>`;
  }

  function bindRowEvents(root, products, moneyFormat) {
    products.forEach((product, index) => {
      const sizeEl = root.querySelector(`[data-yokaa-size="${index}"]`);
      const priceEl = root.querySelector(`[data-yokaa-price="${index}"]`);

      if (sizeEl) {
        sizeEl.addEventListener('change', () => {
          const variant = pickVariant(product, sizeEl.value);
          if (variant && priceEl) {
            priceEl.textContent = money(variant.price, moneyFormat);
          }
        });
      }
    });
  }

  async function addSelectedToCart(root, products) {
    const items = [];

    products.forEach((product, index) => {
      const checked = root.querySelector(`[data-yokaa-check="${index}"]`);
      if (!checked?.checked) return;

      const sizeEl = root.querySelector(`[data-yokaa-size="${index}"]`);
      const variant = pickVariant(product, sizeEl ? sizeEl.value : null);
      if (variant?.id) items.push({ id: variant.id, quantity: 1 });
    });

    if (!items.length) throw new Error('Select at least one item');

    const res = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items }),
    });

    const data = await res.json();
    if (!res.ok) throw new Error(data.description || data.message || 'Could not add to cart');
    return data;
  }

  function afterAddToCart() {
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
      return;
    }

    const notification = document.querySelector('cart-notification');
    if (notification && typeof notification.renderContents === 'function') {
      fetch(`${window.routes?.cart_url || '/cart'}?section_id=cart-notification`)
        .then((r) => r.text())
        .then(() => notification.open?.());
      return;
    }

    window.location.href = window.routes?.cart_url || '/cart';
  }

  async function initRoot(root) {
    const productId = root.dataset.productId;
    const moneyFormat = root.dataset.moneyFormat || undefined;
    let fallback = [];

    try {
      fallback = JSON.parse(root.dataset.fallback || '[]');
    } catch (e) {
      fallback = [];
    }

    const listEl = root.querySelector('[data-yokaa-style-list]');
    const btn = root.querySelector('[data-yokaa-style-btn]');
    const loadingEl = root.querySelector('[data-yokaa-style-loading]');
    const errEl = root.querySelector('[data-yokaa-style-error]');

    try {
      const products = await loadSmartProducts(productId, fallback);

      loadingEl.hidden = true;

      if (!products.length) {
        root.classList.add('is-hidden');
        return;
      }

      listEl.innerHTML = products.map((p, i) => renderProductRow(p, i, moneyFormat)).join('');
      listEl.hidden = false;
      btn.hidden = false;
      bindRowEvents(root, products, moneyFormat);
      root.classList.remove('is-hidden');

      btn.addEventListener('click', async () => {
        btn.disabled = true;
        errEl.textContent = '';
        try {
          await addSelectedToCart(root, products);
          afterAddToCart();
        } catch (err) {
          errEl.textContent = err.message;
        } finally {
          btn.disabled = false;
        }
      });
    } catch (err) {
      root.classList.add('is-hidden');
    }
  }

  function boot() {
    document.querySelectorAll(ROOT_SELECTOR).forEach(initRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
